import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {GetSizeSelectionResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/size_selection/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ContentPopper,
  Flyout,
  FlyoutProps,
  SecondaryContentPopperButton,
  SizeSelector,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {SizeHeader} from '../../../features/SizeSelection/SizeHeader';
import {SizeSection} from '../../../features/SizeSelection/SizeSection';
import {
  SizeSectionSizeReminder,
  useSizeReminderState,
} from '../../../features/SizeSelection/SizeSectionSizeReminder';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';

export interface SoldOutSizeReminderFlyoutViewProps {
  className?: string;
  disableBasketRecommendations?: boolean;
  withFlyout?: boolean;
  renderStickySection?: boolean;
  showStickySection?: boolean;
  sizeSelectionStateManagerKey: SizeSelectionOpenedStateKey;
  isOpenSizeReminder: boolean;
  setIsOpenSizeReminder: (value: boolean) => void;
}

export const SoldOutSizeReminderFlyoutView = ({
  brand,
  product,
  sizeSelection,
  withFlyout,
  sizeSelectionStateManagerKey,
  isOpenSizeReminder,
  setIsOpenSizeReminder,
}: GetSizeSelectionResponse & SoldOutSizeReminderFlyoutViewProps) => {
  const {t} = useTranslation();
  const {
    isOpen: isSizeSelectionOpen,
    cancel: cancelSizeSelection,
    open: openSizeSelection,
  } = useSizeSelectionOpenedState(sizeSelectionStateManagerKey);

  const sizeSelectionType = sizeSelection.type;
  const {showSizeReminder, sizeReminderSize} = useSizeReminderState(
    sizeSelectionType?.$case === 'oneSize' && sizeSelectionType.oneSize.size
      ? [sizeSelectionType.oneSize.size.sizeId]
      : [],
  );

  useEffect(() => {
    if (
      sizeSelectionType?.$case === 'oneSize' &&
      sizeSelectionType.oneSize.size
    ) {
      showSizeReminder(sizeSelectionType.oneSize.size);
    }
  }, [sizeSelectionType, showSizeReminder]);

  const onSizeReminderShowChange = (size: SizeSelection_Size) => {
    showSizeReminder(size);
    cancelSizeSelection();
  };

  const handleOpenerClick = () => {
    openSizeSelection();
  };

  const handleOpenerClose = () => {
    cancelSizeSelection();
  };

  return (
    <>
      {sizeSelection.type?.$case !== 'oneSize' && (
        <StyledContentPopper
          LayoutComponent={PortalSizeSelectionFlyout}
          data-testid="sizeFlyout"
          openerContent={withFlyout && <SizeSelector />}
          headerContent={
            <SizeHeader
              product={product}
              saleBadges={[]}
              productPrice={undefined}
              enableSelectYourSizeTitle
            />
          }
          onOpenerClick={handleOpenerClick}
          onClose={withFlyout ? () => void 0 : handleOpenerClose}
          open={withFlyout ? false : sizeSelection && isSizeSelectionOpen}
          bodyContent={
            !withFlyout &&
            sizeSelection && (
              <SizeSection
                product={product}
                onSizeSelect={() => {}}
                selectedSize={undefined}
                sizeSelection={sizeSelection}
                showSizeReminder={onSizeReminderShowChange}
                disableSoldOut
              />
            )
          }
          footerContent={
            withFlyout && (
              <SecondaryContentPopperButton
                label={t('Close')}
                onClick={handleOpenerClose}
              />
            )
          }
        />
      )}
      <SizeSectionSizeReminder
        brand={brand}
        product={product!}
        SizeReminderLayoutComponent={PortalFlyout}
        sizeReminderSize={sizeReminderSize}
        sizeSelectionOpenedStateKey={sizeSelectionStateManagerKey}
        onResetSizeReminderSize={() => {
          showSizeReminder(undefined);
          setIsOpenSizeReminder(false);
        }}
        isOneSize={sizeSelectionType?.$case === 'oneSize'}
        isOpen={isOpenSizeReminder}
      />
    </>
  );
};

const StyledContentPopper = styled(ContentPopper)`
  header {
    border-bottom-color: ${COLORS.Grey4};
  }
  header[data-space-out-elements='true'] {
    border-bottom-color: ${COLORS.Grey4};
    justify-content: center;
  }
`;

const PortalFlyout = (props: FlyoutProps) => (
  <LayerCorrectedFlyout {...props} />
);

const LayerCorrectedFlyout = styled(Flyout)`
  z-index: ${Z_INDEXES.ADP_QUICK_VIEW_POPUP_ON_TOP_FLYOUT} !important;
`;

const PortalSizeSelectionFlyout = (props: FlyoutProps) => (
  <SizeSelectionFlyout {...props} />
);

const SizeSelectionFlyout = styled(Flyout)`
  z-index: ${Z_INDEXES.NOTIFICATION_CENTER_FLYOUT} !important;
`;
